import $ from "cash-dom";
import validate from "./fieldValidator.js";

export abstract class Validator {
  protected form: HTMLFormElement;
  protected hasErrors: boolean = true;
  protected listenerAttached: boolean = false;
  public triggerValidationEvent: string;
  public successCssClass: string = 'is-validated';
  public errorCssClass: string = 'error'; 

  constructor(form: HTMLFormElement, triggerEvent: string = 'input') {
    this.form = form;
    this.triggerValidationEvent = triggerEvent;
    this.onLoad();
  }

  /**
   * Fields to validate.
   * @returns ValidateJS Constraints
   */
  abstract getConstraints(): Object;
  /**
   * How to proceed when all conditions are met
   * e.g. go to a flow
   */
  abstract onSuccess(): void;

  /**
   * Additional code to run when validation fails
   */
  abstract onError(): void;

  /**
   * Additional code to run when validation succed wihtout submiting
   */
  abstract onChangeEvent(hasError: boolean) : void;

  public onLoad() {
    if (this.triggerValidationEvent) {
      this.registerValidationListener();
    }

    const submitButtons = this.form.querySelectorAll("button[type='submit']");
    Array.from(submitButtons).forEach((button: Element) => {
      button.addEventListener("click", this.onSubmit.bind(this));
    });
  }

  private registerValidationListener() {
    const fields = this.form.querySelectorAll("input,select,textarea");
    Array.from(fields).forEach((field: Element) => {
      field.addEventListener(
        this.triggerValidationEvent, 
        this.onChange.bind(this)
      );
    });
    this.listenerAttached = true;
  }

  public onChange(event: Event) {
    if (event.currentTarget) {
      let input = event.currentTarget as HTMLInputElement;
      this.validateField(input);
      this.hasErrors = this.formHasErrors();
      if (this.hasErrors) {
        $(this.form).addClass('form-error');
      } else {
        $(this.form).removeClass('form-error');
      }
    }
  }
  
  public getFields() {
    const fields = this.form.querySelectorAll("input,select,textarea");
    return Array.from(fields);
  }

  public onSubmit(event: Event) {
    event.preventDefault();
    this.hasErrors = this.validateFields(this.getFields());

    if (!this.hasErrors) {
      this.clearErrors(this.getFields());
      this.onSuccess();
    } else {
      if (!this.listenerAttached) {
        this.triggerValidationEvent = 'input';
        this.registerValidationListener();
      }
      this.onError();
    }
  }

  public validateFields(fields: Element[]): boolean {
    let hasErrors = false;
    fields.forEach(field => {
        const thisField = this.validateField(field as HTMLInputElement);
        hasErrors = hasErrors || thisField;
    });

    return hasErrors;
  }

  public formHasErrors() {
    const fields = this.getFields();
    const fieldsToValidate = {};

    for(let i = 0; i < fields.length; i++) {
      const field = fields[i] as HTMLInputElement;
      const constraint = this.getConstraints()[field.name];
      if (constraint === undefined) {
        continue;
      }

      fieldsToValidate[field.name] = field.value;
    }
    const validation = validate(fieldsToValidate, this.getConstraints(), { format: 'flat'});
    return validation && validation.length > 0;
  }

  public validateField(field: HTMLInputElement): boolean {
    const constraint = this.getConstraints()[field.name];
    if (constraint === undefined) {
      return;
    }
    const validation = validate.single(field.value, constraint);
    const parent = $(field).parent();
    let hasErrors = false;
    
    if (validation) {
      hasErrors = true;
      $(field).addClass(this.errorCssClass);
      $(field).removeClass(this.successCssClass);
      this.showMessage(field, validation[0]);
    } else {
      hasErrors = false;
      $(field).addClass(this.successCssClass);
      $(field).removeClass(this.errorCssClass);
      parent.children('label[for="'+ field.name + '"].error').remove();
    }

    this.onChangeEvent(hasErrors);
    return hasErrors;
  }

  private showMessage(field: HTMLInputElement, message: string) {
    let messageDOM = $(field).siblings('label[for="'+ field.name + '"].error');
    if (messageDOM.exists()) {
      messageDOM.text(message);
    } else {
      let dom = document.createElement('label');
      dom.innerText = message;   
      dom.setAttribute('class', 'error');
      dom.setAttribute('for', field.name);
      $(field).after(dom);
    }
  }

  public clearErrors(fields: Element[]): void {
    $(this.form).removeClass('form-error');
    fields.forEach((field: HTMLInputElement) => {
      const parent = $(field).parent();
      $(field).addClass(this.successCssClass);
      $(field).siblings('label[for="'+ field.name + '"].error').remove();
      $(field).removeClass(this.errorCssClass);
    });
  }

  static encode(input: string) {
    return encodeURIComponent(btoa(input));
  }
}
